import InsightPickListCard from "features/insights/components/cards/insight-pick-list-card";
import EditInsights from "features/insights/modals/EditInsights";
import { InsightTypeFilter } from "features/journey-map/modals/AdvancedSearch/InsightTypeFilter";
import { JourneyFilter } from "features/journey-map/modals/AdvancedSearch/JourneyFilter";
import { PersonaFilter } from "features/journey-map/modals/AdvancedSearch/PersonaFilter";
import { CheckBox, EmptyIconContainer, EmptyList, FiltersContainer, ListItem, ListItemsContainer } from "features/journey-map/modals/AdvancedSearch/Style";
import { TagFilter } from "features/journey-map/modals/AdvancedSearch/TagFilter";
import { TouchpointFilter } from "features/journey-map/modals/AdvancedSearch/TouchpointFilter";
import { memo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { List } from "react-virtualized";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { createInsight } from "redux/index";
import { Button, GenericButton, IconDispatcher, SearchBar } from "shared/ui-basics";
import styled from "styled-components/macro";
import { createDefaultInsight, encode } from "utils/helpers";
import { useModal } from "use-modal-hook";

// Types for props and other elements
interface InsightAdvancedSearchModalProps {
	isOpen: boolean;
	onClose: () => void;
	link: (ids: string[], event: any) => void;
	selectedInsightsIds?: string[];
}

const InsightAdvancedSearchModal = memo(({ isOpen, onClose, link, selectedInsightsIds = [] }: InsightAdvancedSearchModalProps) => {
	const { t } = useTranslation();

	const insightTypeRef = useRef<any>(null);
	const personaRef = useRef<any>(null);
	const cjmRef = useRef<any>(null);
	const touchpointRef = useRef<any>(null);
	const tagRef = useRef<any>(null);

	const userInfo = useSelector(({ auth }: any) => auth?.userInfo);
	const showSampleData = useSelector((state: any) => state.settings.organization.showSampleData);
	const insights: any[] = useSelector((state: any) => state?.insights?.all_insights || [])
		.getActiveRecords(!showSampleData)
		?.filter((insight: any) => !selectedInsightsIds?.includes(insight?.id));

	const [filteredInsights, setFilteredInsights] = useState<any[]>(insights);
	const [selectedIds, setSelectedIds] = useState<string[]>([]);
	const [searchedValue, setSearchedValue] = useState<string>("");

	const [showEditModal] = useModal(EditInsights);

	const applyFilters = () => {
		const insightTypeFilters = insightTypeRef?.current?.getFilters();
		const personaFilters = personaRef?.current?.getFilters();
		const cjmFilters = cjmRef?.current?.getFilters();
		const touchpointFilters = touchpointRef?.current?.getFilters();
		const tagFilters = tagRef?.current?.getFilters();

		const filtered = insights?.filter((insight) => {
			return (
				(!insightTypeFilters?.length || insightTypeFilters.includes(insight?.typeId)) &&
				(!personaFilters?.length || personaFilters.some((id: string) => insight?.linkedPersonas?.includes(id))) &&
				(!cjmFilters?.length || cjmFilters.some((id: string) => Object.keys(insight?.linkedCjms || {}).includes(id))) &&
				(!touchpointFilters?.length || touchpointFilters.some((id: string) => insight?.linkedTouchpoints?.includes(id))) &&
				(!tagFilters.length || tagFilters.some((id: string) => insight?.tags?.includes(id)))
			);
		});

		setFilteredInsights(filtered);
	};

	const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setSearchedValue(value);
	};

	const handleSelectInsight = (id: string) => {
		if (!selectedIds?.includes(id)) setSelectedIds([...selectedIds, id]);
		else setSelectedIds(selectedIds.filter((sId) => sId !== id));
	};

	const addSelected = (e: React.MouseEvent) => {
		if (onClose) onClose();
		link(selectedIds, e);
		// selectedIds?.forEach((selectedId) => link(selectedId, e));
	};

	const recordsToDisplay = filteredInsights?.filter((is) => is?.name?.toLowerCase()?.includes(searchedValue)).sort((a, b) => a.name.localeCompare(b.name));

    const createNewInsight = () => {
        if (onClose) onClose();
		const item = createDefaultInsight({name: encode(searchedValue.truncate(100, true)), currentUser: userInfo });
        const onLinkSuccess = () => showEditModal({ insightId: item.id });
		createInsight({ item, onSuccess: () => link(item.id, onLinkSuccess) });
	};

	return (
		<ModalContainer fade={false} isOpen={isOpen} toggle={onClose} size="sm" centered={true}>
			<ModalHeader toggle={onClose}>{t("ADVANCED_SEARCH")}</ModalHeader>
			<ModalBody className="p-0">
				<FiltersContainer>
					<InsightTypeFilter innerRef={insightTypeRef} handleApply={applyFilters} />
					<PersonaFilter innerRef={personaRef} handleApply={applyFilters} />
					<JourneyFilter innerRef={cjmRef} handleApply={applyFilters} />
					<TouchpointFilter innerRef={touchpointRef} handleApply={applyFilters} />
					<TagFilter innerRef={tagRef} handleApply={applyFilters} />
					<SearchBar globalFilterValue={searchedValue} handleChange={handleSearch} height={34} />
				</FiltersContainer>
				<ListItemsContainer>
					{recordsToDisplay?.length > 0 ? (
						<List
							width={1100}
							height={450}
							rowCount={recordsToDisplay?.length}
							rowHeight={105}
							rowRenderer={({ index, style }: { index: number; style: React.CSSProperties }) => (
								<ListItem
									key={recordsToDisplay[index].id}
									style={style}
									id={`searchPickList${recordsToDisplay[index].id}`}
									onClick={() => handleSelectInsight(recordsToDisplay[index].id)}>
									<div className="h-100 d-flex align-self-center">
										<CheckBox type="checkbox" checked={selectedIds?.includes(recordsToDisplay[index].id)} onChange={() => {}} />
									</div>
									<InsightPickListCard insight={recordsToDisplay[index]} />
								</ListItem>
							)}
						/>
					) : (
						<EmptyList>
							<EmptyIconContainer>{IconDispatcher("magnifying-glass", "", { style: "thin" })}</EmptyIconContainer>
							<div>{t("NO_RECORD_TO_DISPLAY")}</div>
							<GenericButton variant="dark" icon="plus" onClick={createNewInsight}>
								{t("ADD_INSIGHT")}
							</GenericButton>
						</EmptyList>
					)}
				</ListItemsContainer>
			</ModalBody>
			<ModalFooter>
				<Button variant="dark-outline" size="sm" onClick={onClose}>
					{t("JS_47")}
				</Button>
				<Button variant="dark" size="sm" onClick={addSelected} disabled={selectedIds?.length === 0}>
					{t("ADD_SELECTED")}
				</Button>
			</ModalFooter>
		</ModalContainer>
	);
});

InsightAdvancedSearchModal.displayName = "InsightAdvancedSearchModal";
export { InsightAdvancedSearchModal };

const ModalContainer = styled(Modal)`
	max-width: 1140px;
`;
